import { Body, Caption, Card, cn } from '@vrzl/ui';

import { percentage, type ValidationCounts, type ValidationFilter } from '../utils/validationView';

const SEGMENTS = [
  { key: 'consistent', label: 'Aanwezig', color: 'bg-success-600' },
  { key: 'inconsistent', label: 'Inconsistent', color: 'bg-warning-500' },
  { key: 'missing', label: 'Niet aanwezig', color: 'bg-error-600' },
] as const;

function StatusBar({ counts }: { counts: ValidationCounts }) {
  return (
    <>
      <div aria-hidden="true" className="flex h-2 min-w-48 flex-1 gap-0.5 overflow-hidden rounded-full bg-muted">
        {SEGMENTS.map(
          (segment) =>
            counts[segment.key] > 0 && (
              <div
                key={segment.key}
                className={segment.color}
                style={{ flexGrow: counts[segment.key] }}
              />
            ),
        )}
      </div>
      <ul className="flex flex-wrap items-center gap-4">
        {SEGMENTS.map((segment) => (
          <li key={segment.key} className="flex items-center gap-1.5">
            <span aria-hidden="true" className={cn('size-2 rounded-full', segment.color)} />
            <Caption as="span">{segment.label}</Caption>
            <Caption as="span" className="font-semibold tabular-nums text-foreground">
              {percentage(counts[segment.key], counts.all)}% ({counts[segment.key]})
            </Caption>
          </li>
        ))}
      </ul>
    </>
  );
}

function PercentageBar({
  filter,
  counts,
}: {
  filter: 'present' | 'missing';
  counts: ValidationCounts;
}) {
  const isPresent = filter === 'present';
  const part = isPresent ? counts.present : counts.missing;
  const pct = percentage(part, counts.all);
  return (
    <>
      <div className="flex items-center gap-3">
        <span className="text-4xl font-bold tabular-nums text-foreground">{pct}%</span>
        <Body as="span" tone="muted">
          {part} van {counts.all} criteria {isPresent ? 'aanwezig' : 'niet aanwezig'}
        </Body>
      </div>
      <div aria-hidden="true" className="h-2 min-w-48 flex-1 overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full', isPresent ? 'bg-success-600' : 'bg-error-600')}
          style={{ width: `${pct}%` }}
        />
      </div>
    </>
  );
}

type ValidationSummaryProps = {
  filter: ValidationFilter;
  counts: ValidationCounts;
  activeCriteria: number;
  totalCriteria: number;
};

/** Eén kaart, alles op één lijn: statusbalk (Alles) of percentage (Aanwezig/Niet aanwezig). */
export function ValidationSummary({
  filter,
  counts,
  activeCriteria,
  totalCriteria,
}: ValidationSummaryProps) {
  return (
    <Card padding="lg" className="mb-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        {filter === 'all' ? (
          <StatusBar counts={counts} />
        ) : (
          <PercentageBar filter={filter} counts={counts} />
        )}
        <Body as="span" tone="muted" className="ml-auto">
          {activeCriteria} van {totalCriteria} criteria actief
        </Body>
      </div>
    </Card>
  );
}
